import { useDateFormatter, useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import { SelectOption } from 'components/inputs/BaseSelect';
import SinglePunchAssignees from 'components/inputs/dropdowns/AssigneesTabsMultiSelect/SinglePunchAssignees';
import Label from 'components/inputs/Label';
import PrioritySelect from 'components/inputs/PrioritySelect';
import StatusSelect from 'components/inputs/StatusSelect';
import { Button, Modal, ModalContent, ModalFooter } from 'components/lib';
import ConfirmModal from 'components/modals/ConfirmModal';
import { InsertFilesSubmitParams } from 'components/modals/InsertFilesModal/InsertFilesModal';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlayPunchPhotos, {
  PunchPhoto,
} from 'components/Overlays/OverlayPunchPhotos';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import OverlayViewerContainer from 'components/Overlays/OverlayViewerContainer';
import Tabs from 'components/Tabs';
import Tab from 'components/Tabs/Tab';
import AppStoreContext from 'contexts/AppStoreContext';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import Immutable from 'immutable';
import {
  ManageDrawingsSupportContext,
  ManageDrawingsSupportValue,
} from 'providers/ManageDrawingsSupportProvider';
import React, { useContext, useEffect, useState } from 'react';
import {
  RouteComponentProps,
  useHistory,
  useLocation,
  withRouter,
} from 'react-router-dom';
import { UpdatePunch } from 'records/interactions/UpdatePunch';
import { ViewMapPunchList } from 'records/interactions/ViewMapPunchList';
import { ViewPunchList } from 'records/interactions/ViewPunchList';
import { ModalState } from 'records/ModalState';
import { Photo } from 'records/Photo';
import { Project } from 'records/Project';
import { Punch, PunchStatusValue } from 'records/Punch';
import { Sheet } from 'records/Sheet';
import { SheetVersion } from 'records/SheetVersion';
import { getParentSheet } from 'services/markupService';
import { getPhoto } from 'services/photoService';
import {
  addInsertPhotos,
  deleteInsertedPhoto as deleteInsertedPhotoService,
  deleteInsertedPhotos as deleteInsertedPhotosService,
  deletePunchPhotos as deletePunchPhotosService,
} from 'services/punchService';
import { getDataURL } from 'utils/general';
import { isBasicOrUnpaidTier } from 'utils/license';
import * as toast from 'utils/toast';
import { clearQueryParam } from 'utils/url';
import { PunchQuery } from '../../../records/PunchQuery';
import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from '../MapOverlayMachine';
import Assignees from './Assignees';
import Comments from './Comments';
import Description from './Description';
import DueDate from './DueDate';
import Location from './Location';
import Snapshot from './Snapshot';

type TabType = 'Overview' | 'Comments';

interface UpdatePunchOverlayProps extends RouteComponentProps {
  interaction: UpdatePunch;
}

const UpdatePunchOverlay: React.FC<UpdatePunchOverlayProps> = ({
  interaction,
}) => {
  const { userId } = useAuth();
  const { translate } = useTranslator([
    'mapView',
    'buttons',
    'updatePunchOverlay',
    'disciplineFilterSection',
    'formLabels',
  ]);
  const dateFormatter = useDateFormatter({
    dateStyle: 'medium',
  });

  const { state: appState, actions } = useContext(AppStoreContext);
  const [punch, setPunch] = useState<Punch>(interaction.punch);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [arePhotosLoaded, setArePhotosLoaded] = useState(false);
  const [punchPhotos, setPunchPhotos] = useState<PunchPhoto[]>([]);
  const [snapshotParentPage, setSnapshotParentPage] =
    useState<SheetVersion | null>(null);
  const { sheets, selectedSheet, punchComments, userLicense, punchTableQuery } =
    appState;
  const history = useHistory();
  const location = useLocation();
  const { mapOverlayMachineService } = useMapOverlayMachine();
  const [isModalShown, setIsModalShown] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>('Overview');
  const [comment, setComment] = useState('');
  const [updateDisabled, setUpdateDisabled] = useState<boolean>(true);
  const [deletedPhotoids, setDeletedPhotoids] = useState<string[]>([]);
  const {
    [FeatureFlag.EssentialsDrawingEditor]: isEssentialsDrawingEditorEnabled,
    [FeatureFlag.UserGroups]: isUserGroupsEnabled,
  } = useFeatureFlags();

  const loadSnapshotParentPage = async (): Promise<void> => {
    if (interaction.punch.markupids.count() === 0) {
      return;
    }
    const markupid = interaction.punch.markupids.first<string>();

    const rawSheet = await getParentSheet(
      markupid,
      interaction.punch.projectid
    );
    const sheet = SheetVersion.deserialize(rawSheet);

    setSnapshotParentPage(sheet);
  };

  const loadPhotos = async (): Promise<void> => {
    const photoLoads: Photo[] = await Promise.all(
      punch.photoids.map(async (photoid: string): Promise<Photo> => {
        const photo = await getPhoto(punch.projectid, photoid);

        return photo;
      })
    );

    setPhotos(photoLoads);
    setArePhotosLoaded(true);
  };

  const loadComments = async () => {
    await actions.getCommentsForIssue(punch.id);
  };

  useEffect(() => {
    loadPhotos();
    loadSnapshotParentPage();
    loadComments();
    return () => {
      actions.endEditing();
      actions.clearPunchComments();
    };
  }, []);

  useEffect(() => {
    // Allows deletion of loaded photos
    setPunchPhotos((prev) =>
      prev.concat(
        photos.map<PunchPhoto>((p) => ({
          id: p.fileid,
          url: p.url,
          name: p.filename,
        }))
      )
    );
  }, [photos]);

  useEffect(() => {
    setUpdateDisabled(
      !arePhotosLoaded ||
        (punchPhotos.length === punch.photoids.count() &&
          interaction.punch === punch) ||
        punch.location.length === 0
    );
  }, [punchPhotos, interaction.punch, punch, arePhotosLoaded]);

  const closeUpdatePunchOverlay = () => {
    if (location.pathname.includes('map')) {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
    } else {
      actions.deleteInteractions('UpdatePunch');
    }
  };

  const handleBackArrowClick = (): void => {
    const sheet = sheets.get(selectedSheet, new Sheet());

    if (location.pathname.includes('map')) {
      const toggledSheets = appState
        .getVisibleSheetEntries()
        .valueSeq()
        .toList()
        .map((entry) => entry.sheet);
      const punchListInteraction = new ViewMapPunchList({
        sheets: toggledSheets,
        containerStyles: {
          top: 96,
          height: `calc(100vh - 96px)`,
        },
        resetPunchQuery: false,
      });
      mapOverlayMachineService.send({
        type: MapOverlayMachineEventsEnum.OPEN_MAP_PUNCH_LIST_OVERLAY,
        data: { interaction: punchListInteraction },
      });
    } else {
      const viewPunchInteraction = new ViewPunchList({
        sheet,
        resetPunchQuery: false,
      });
      actions.deleteInteractions('UpdatePunch');
      actions.createInteraction(viewPunchInteraction);
    }
  };

  const clearPunchQueryParam = (): void => {
    clearQueryParam('punch', history);
  };

  const handlePhotoUpload = async (files: FileList): Promise<void> => {
    const uploadedPunchPhotos = await Promise.all(
      [...Array.from(files)].map(async (f) => {
        const url = await getDataURL(f);

        return {
          file: f,
          url,
          name: f.name,
        };
      })
    );

    setPunchPhotos((prev) => prev.concat(uploadedPunchPhotos));
  };

  const handlePhotoInsert = async (params: InsertFilesSubmitParams) => {
    const { selectedProject } = appState;
    const { success, photos: insertedPhotos } = await addInsertPhotos(
      selectedProject,
      params
    );

    if (!success) {
      toast.error(
        translate('toastText:fileUploadError.message'),
        translate('toastText:fileUploadError.title')
      );
    }

    const insertedPunchPhotos: PunchPhoto[] = insertedPhotos.map((photo) => ({
      id: photo.fileid,
      name: photo.name,
      url: photo.url,
    }));

    setPunchPhotos((prev) => prev.concat(insertedPunchPhotos));
  };

  const deleteUploadedPhoto = (photoToDelete: PunchPhoto): void => {
    setPunchPhotos((prev) =>
      prev.reduce<PunchPhoto[]>((updated, currentPhoto) => {
        if (currentPhoto.url !== photoToDelete.url) updated.push(currentPhoto);

        return updated;
      }, [])
    );
  };

  const deleteLoadedOrInsertedPhoto = async (
    photoToDelete: PunchPhoto
  ): Promise<void> => {
    const { selectedProject } = appState;
    const photoIdToDelete = photoToDelete.id as string; // ESLint hates id!
    await deleteInsertedPhotoService(selectedProject, photoIdToDelete);

    setPunchPhotos((prev) =>
      prev.reduce<PunchPhoto[]>((updated, currentPhoto) => {
        if (currentPhoto.id !== photoIdToDelete) updated.push(currentPhoto);

        return updated;
      }, [])
    );

    if (punch.photoids.contains(photoIdToDelete))
      setPunch(
        punch.updateIn(
          ['photoids'],
          (photoIds: Immutable.List<string>): Immutable.List<string> =>
            photoIds.filterNot((photoId) => photoId === photoIdToDelete)
        )
      );
  };

  const handlePhotoDelete = async (
    photoToDelete: PunchPhoto
  ): Promise<void> => {
    if (photoToDelete.id) {
      deletedPhotoids.push(photoToDelete.id);
      setDeletedPhotoids(deletedPhotoids);
      deleteLoadedOrInsertedPhoto(photoToDelete);
    } else {
      deleteUploadedPhoto(photoToDelete);
    }
  };

  const filterInInsertedPhotos = (allPunchPhotos: PunchPhoto[]): string[] => {
    return allPunchPhotos.reduce<string[]>((inserted, { id }) => {
      if (id && !punch.photoids.contains(id)) inserted.push(id);
      return inserted;
    }, []);
  };

  const clearInsertedPhotos = async () => {
    if (punchPhotos.length > 0) {
      const { selectedProject } = appState;

      // TODO: possible toast: if result.length < punchPhotos.length, then some deletions failed
      await deleteInsertedPhotosService(
        selectedProject,
        filterInInsertedPhotos(punchPhotos)
      );
    }
  };

  const handleCancelClick = (): void => {
    if (
      interaction.punch !== punch ||
      punchPhotos.length > punch.photoids.count() ||
      comment !== ''
    ) {
      const modalState = new ModalState({
        id: 'ConfirmPunchUpdate',
        portal: 'main',
        open: true,
        shouldCloseOnEsc: false,
        shouldCloseOnOverlayClick: false,
        component: ConfirmModal,
        props: {
          id: 'confirm-punch-update-modal',
          acceptButtonText: translate('mapView:updatePunchOverlay.discard'),
          acceptButtonType: 'delete',
          denyButtonText: translate('buttons:cancel'),
          headerText: translate('mapView:updatePunchOverlay.areYouSure'),
          content: translate('mapView:updatePunchOverlay.unsavedChangesText'),
          acceptCallback: (): void => {
            closeUpdatePunchOverlay();
            actions.clearAllModals();
            clearPunchQueryParam();
            clearInsertedPhotos();
          },
          denyCallback: (): void => {
            actions.clearAllModals();
          },
        },
      });

      actions.createModal(modalState);
    } else {
      closeUpdatePunchOverlay();
      clearPunchQueryParam();
    }
    actions.endEditing();
  };

  const handlePunchChange = (
    field: any,
    val: string | number | Immutable.OrderedSet<string> | PunchStatusValue
  ): void => {
    const updatedPunch = punch.set(field, val);
    setPunch(updatedPunch);
    actions.beginEditing();
  };

  const deleteModal = (
    <Modal
      size="small"
      title={translate('updatePunchOverlay:deletePunchModalFields.headerText')}
      onDismiss={() => setIsModalShown(false)}
    >
      <ModalContent>
        <p>
          {translate(
            'updatePunchOverlay:deletePunchModalFields.descriptionText'
          )}
        </p>
        <p className="bold">
          {translate(
            'updatePunchOverlay:deletePunchModalFields.confirmChoiceText'
          )}
        </p>
      </ModalContent>
      <ModalFooter>
        <Button
          id="delete-punch-deny-btn"
          onClick={() => setIsModalShown(false)}
          variant="tertiary"
          size="large"
        >
          {translate('updatePunchOverlay:deletePunchModalFields.cancelBtnText')}
        </Button>
        <Button
          id="delete-punch-accept-btn"
          variant="destructive"
          onClick={() => {
            actions.deletePunch(interaction.punch);
            closeUpdatePunchOverlay();
            setIsModalShown(false);
            clearPunchQueryParam();
          }}
          size="large"
        >
          {translate(
            'updatePunchOverlay:deletePunchModalFields.deleteButtonText'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );

  const HandleDeletePunch = (): void => setIsModalShown(true);

  const groupPhotosByType = (
    allPunchPhotos: PunchPhoto[]
  ): { loadedAndInserted: string[]; uploaded: File[] } => {
    const uploaded: File[] = [];
    // 'loaded' photos as in 'the photos that came with the punch when it was opened for edit'
    const loadedAndInserted: string[] = [];

    allPunchPhotos.forEach(({ id, file }) => {
      if (file) uploaded.push(file);
      else if (id) loadedAndInserted.push(id);
      else throw new Error('Invalid punch photo'); // should never get here
    });

    return {
      uploaded,
      loadedAndInserted,
    };
  };

  const handleSave = async (): Promise<void> => {
    const {
      loadedAndInserted: loadedAndInsertedPhotoIds,
      uploaded: uploadedPhotos,
    } = groupPhotosByType(punchPhotos);

    if (deletedPhotoids.length > 0) {
      await deletePunchPhotosService(
        punch.projectid,
        punch.id,
        deletedPhotoids
      );
    }

    if (
      punch !== interaction.punch ||
      uploadedPhotos.length > 0 ||
      loadedAndInsertedPhotoIds.length > 0
    ) {
      await actions.updatePunch(
        punch,
        uploadedPhotos,
        loadedAndInsertedPhotoIds
      );

      const lastMarkupId = punch.markupids.last('');

      if (lastMarkupId.length > 0) {
        await actions.getMarkup(punch.markupids.last(), interaction.fileid);
      }
    }

    closeUpdatePunchOverlay();
    clearPunchQueryParam();

    //if filters are applied then reset filters on punch update
    if (punchTableQuery.filterApplied()) {
      actions.setPunchTableQuery(new PunchQuery());
    }

    actions.endEditing();
  };

  const { projects, selectedProject, users, projectGroups } = appState;

  // Determine if app is in Punches page or SheetViewer
  const notInDocumentViewer = selectedSheet === '';

  const { permissions, disciplines, assignees, priorities } = projects.get(
    selectedProject,
    new Project()
  );
  const currentUser = users.find((u) => userId === u.id);
  const isPunchAuthor = punch.author.email === currentUser?.email;
  const isMember = appState.getUserRole(userId) === 'member';
  const basicOrUnpaidTier = isBasicOrUnpaidTier(userLicense.package);

  const canDeletePunch =
    permissions.deletePunch ||
    (isPunchAuthor && isMember && !basicOrUnpaidTier);

  const OverlayContainer = interaction.viewerOverlay
    ? OverlayViewerContainer
    : OverlayStdContainer;

  const handleTabChange = (tab: string): void => {
    setSelectedTab(tab as TabType);
  };

  const handleSaveComment = () => {
    actions.addComment(
      punch.id,
      punch.projectid,
      comment.trim(),
      Math.floor(Date.now() / 1000)
    );
    setComment('');
  };

  const saveCommentDisabled = (): boolean => comment.length === 0;

  const getCurrentDisciplines = (): SelectOption[] => {
    const disciplineArray: string[] = [];
    punch.disciplines.forEach((discipline: string) => {
      disciplineArray.push(discipline);
    });

    return disciplineArray.map((discipline: string): SelectOption => {
      const translatedDiscipline: `${string}:${string}` =
        `disciplineFilterSection:disciplines.${discipline}` as `${string}:${string}`;
      return {
        label: translate(translatedDiscipline, {
          defaultValue: discipline,
        }),
        value: discipline,
      };
    });
  };

  const disciplinesArray = disciplines.toArray();
  const translatedDisciplinesOptions = disciplinesArray.map((discipline) => {
    const translatedDiscipline: `${string}:${string}` =
      `disciplineFilterSection:disciplines.${discipline}` as `${string}:${string}`;
    return {
      value: discipline,
      label: translate(translatedDiscipline, {
        defaultValue: discipline,
      }),
    };
  });

  const renderTab = () => {
    if (selectedTab === 'Overview') {
      return (
        <>
          <div className="column justify-between oa grow-1">
            <div className="mt4 ml4 mr4">
              <div className="mb4">
                <Location
                  editable={permissions.editLocation}
                  value={punch.location}
                  onChange={({
                    target: { value },
                  }: React.ChangeEvent<HTMLInputElement>): void =>
                    handlePunchChange('location', value)
                  }
                />
              </div>
              {punch.markupids.count() > 0 ? (
                <div className="grow-1 mb3">
                  <ManageDrawingsSupportContext.Consumer>
                    {({ onSuccess }: ManageDrawingsSupportValue) => (
                      <Snapshot
                        punch={punch}
                        sheetnumber={
                          snapshotParentPage
                            ? snapshotParentPage.sheetnumber
                            : ''
                        }
                        label={
                          snapshotParentPage
                            ? `${snapshotParentPage.sheetnumber}: ${snapshotParentPage.sheettitle}`
                            : ''
                        }
                        onClick={(
                          projectId: string,
                          sheetId: string,
                          markupId: string
                        ) => {
                          onSuccess(async () => {
                            if (notInDocumentViewer) {
                              if (isEssentialsDrawingEditorEnabled) {
                                history.push(
                                  `/project/${projectId}/drawing/${sheetId}/editor`
                                );
                              } else {
                                history.push(
                                  `/project/${projectId}/sheet/${sheetId}/view?markup=${markupId}`
                                );
                              }
                            } else {
                              await actions.setFocusedMarkup(markupId);
                            }
                          });
                        }}
                      />
                    )}
                  </ManageDrawingsSupportContext.Consumer>
                </div>
              ) : null}
              <div className="mb3">
                <Description
                  editable={permissions.editDescription}
                  value={punch.description}
                  onChange={({
                    target: { value },
                  }: React.ChangeEvent<HTMLTextAreaElement>): void =>
                    handlePunchChange('description', value)
                  }
                />
              </div>
              <div className="row mb3">
                <div className="grow-1 w100 mr3">
                  <div className="grow-1 w100 mr3">
                    <Label label={translate('mapView:labels.priority')} />
                    <PrioritySelect
                      id="new-punch-overlay-priority-select"
                      value={punch.priority}
                      options={priorities}
                      onChange={(priority: string) =>
                        handlePunchChange('priority', priority)
                      }
                    />
                  </div>
                </div>
                <div className="grow-1 w100">
                  <Label label={translate('mapView:labels.status')} />
                  <StatusSelect
                    id="update-punch-overlay-status"
                    value={punch.status}
                    onChange={(value: PunchStatusValue): void =>
                      handlePunchChange('status', value)
                    }
                  />
                </div>
              </div>
              <div className="mb3 pr2 w50 update-punch-duedate">
                <style>
                  {`
                    .update-punch-duedate .react-datepicker-wrapper {
                      width: 100%;
                    }

                    .update-punch-duedate .react-datepicker-popper {
                      margin-left: -20px
                    }
                  `}
                </style>
                <DueDate
                  editable={permissions.editDueDate}
                  value={punch.duedate}
                  onChange={(date: Date): void => {
                    const val = date ? date.getTime() / 1000 : 0;
                    handlePunchChange('duedate', val);
                  }}
                />
              </div>
              <div className="mb3">
                <Label label={translate('mapView:labels.assignee')} />
                {isUserGroupsEnabled ? (
                  <SinglePunchAssignees
                    id="new-punch-overlay-assignee-select"
                    assigneesValues={punch.assignees}
                    groupAssigneesValues={punch.groupAssignees}
                    assigneesOptions={assignees}
                    groupAssigneesOptions={projectGroups}
                    editable={permissions.editAssignee}
                    onChange={(
                      updatedAssignees: Immutable.OrderedSet<string>,
                      type: string
                    ): void => {
                      handlePunchChange(type, updatedAssignees);
                    }}
                  />
                ) : (
                  <Assignees
                    editable={permissions.editAssignee}
                    values={punch.assignees}
                    options={assignees.sort()}
                    onChange={(
                      updatedAssignees: Immutable.OrderedSet<string>
                    ): void => handlePunchChange('assignees', updatedAssignees)}
                  />
                )}
              </div>
              <OverlayPunchPhotos
                id="update-punch-overlay-photos"
                punchPhotos={punchPhotos}
                onPhotoUpload={handlePhotoUpload}
                onPhotoInsert={handlePhotoInsert}
                onPhotoDelete={handlePhotoDelete}
              />
              <div
                className="mt1 mb1 w100 bt-3 font-size-4 black-2 py3"
                style={{ marginTop: '18px' }}
              >
                {`${translate(
                  'mapView:updatePunchOverlay.createdOn'
                )} ${dateFormatter?.format(
                  new Date(punch.dbcreateddate * 1000)
                )} ${translate('mapView:updatePunchOverlay.by')} ${
                  punch.author.email
                }`}
              </div>
            </div>
          </div>
          <OverlayFooter
            id="update-punch-overlay"
            confirmText={translate('buttons:save')}
            cancelText={translate('buttons:cancel')}
            onCancelClick={handleCancelClick}
            onConfirmClick={handleSave}
            confirmDisabled={updateDisabled}
          >
            {canDeletePunch && (
              <Button
                id="update-punch-overlay-delete-btn"
                size="small"
                variant="destructive"
                onClick={HandleDeletePunch}
              >
                {translate('mapView:updatePunchOverlay.deleteItem')}
              </Button>
            )}
          </OverlayFooter>
        </>
      );
    }

    if (selectedTab === 'Comments') {
      return (
        <>
          <Comments
            comments={punchComments.sort((c1, c2) => c2.date - c1.date)}
            comment={comment}
            setComment={setComment}
          />
          <OverlayFooter
            id="update-punch-overlay"
            confirmText={translate('buttons:submit')}
            onConfirmClick={handleSaveComment}
            confirmDisabled={saveCommentDisabled()}
            hideCancel
          />
        </>
      );
    }

    return null;
  };

  return (
    <>
      {isModalShown && deleteModal}
      <OverlayContainer
        id="update-punch-overlay"
        style={interaction.containerStyles}
      >
        <OverlayHeader
          id="update-punch-overlay-header"
          backArrow={interaction.viewerOverlay}
          onBackArrowClick={handleBackArrowClick}
          title={`${translate('mapView:updatePunchOverlay.itemId')}: ${
            punch.number
          }`}
          onCancelClick={handleCancelClick}
        />
        <div className="pt3">
          <Tabs
            tabNames={['Overview', 'Comments']}
            onChange={handleTabChange}
            selectedTab={selectedTab}
          >
            <Tab
              name="Overview"
              placeholder={translate('mapView:labels.overview')}
            />
            <Tab
              name="Comments"
              placeholder={translate('mapView:labels.comments')}
              activeCount={punchComments.size}
            />
          </Tabs>
        </div>
        {renderTab()}
      </OverlayContainer>
    </>
  );
};

export default withRouter(UpdatePunchOverlay);
